// The rules of the sidebar and of ⌘K's recent visits, without React (ADR-114).

/**
 * What the sidebar remembers a place or a home queue by, pinned or visited: a stable id, never a label, so a renamed
 * place stays pinned and its recent visits stay counted.
 */
export const placeKey = (id: string) => `place:${id}`;
export const queueKey = (id: string) => `queue:${id}`;

export function parseKey(key: string): { kind: "place" | "queue"; id: string } | undefined {
  const [kind, id] = key.split(":");
  return (kind === "place" || kind === "queue") && id ? { kind, id } : undefined;
}

/** A place is on display on its route and on the routes below it (a linesheet is in Linesheets). */
export const isActivePlace = (to: string, pathname: string): boolean =>
  to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`);

/** The "your turn" count of every place: the sum of the queues that open it. */
export function placeCounts(queues: { place: string; count: number }[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const { place, count } of queues) counts[place] = (counts[place] ?? 0) + count;
  return counts;
}

export const sumOf = (places: { id: string }[], counts: Record<string, number>): number =>
  places.reduce((sum, { id }) => sum + (counts[id] ?? 0), 0);

/** A page the user opened: a place of the sidebar, or an entity (a PO, a linesheet) with the name it had. */
export interface RecentVisit {
  /** `place:<id>` or the path of the entity. */
  key: string;
  /** The place it belongs to: its icon and its section. */
  place: string;
  href: string;
  /** The name of an entity; a place takes its name from the sidebar, so a renamed place is not stale. */
  label?: string;
  count: number;
  last: number;
}

/** A visit weighs half after three days (frecency: how often, discounted by how long ago). */
const HALF_LIFE_MS = 3 * 24 * 60 * 60 * 1000;
const KEPT = 30;

const score = (visit: RecentVisit, now: number) => visit.count * 2 ** (-(now - visit.last) / HALF_LIFE_MS);

/** The visits by frecency, the most worth reopening first. */
const rankRecent = (visits: RecentVisit[], now: number): RecentVisit[] =>
  [...visits].sort((a, b) => score(b, now) - score(a, now));

/** Counts one more visit, with the latest name and link; the list by frecency, and only the `KEPT` worth most. */
export function recordVisit(
  visits: RecentVisit[],
  visit: Omit<RecentVisit, "count" | "last">,
  now: number,
): RecentVisit[] {
  const known = visits.find((v) => v.key === visit.key);
  const updated: RecentVisit = { ...visit, count: (known?.count ?? 0) + 1, last: now };
  return rankRecent([updated, ...visits.filter((v) => v.key !== visit.key)], now).slice(0, KEPT);
}
